<template>
  <HgModal
    v-model="internalModel"
    closeButtonCssClass="export-csv-modal-close-button"
    :closeOnBackgroundClick="!isLoading"
    :closeOnEscapePressed="!isLoading"
    :hideCloseButton="isLoading"
    @close="userClose()"
  >
    <template #title>{{ t('general.buttons.exportLeads') }}</template>

    <template #default>
      <div v-if="!isLoading">
        <h4 class="time-frame-title">
          {{ t(`csvExport.timeframe`) + ':' }}
        </h4>
        <TimeframeOptionsRadio
          v-model="selectedTimeframe"
          @update:model-value="handleTimeframeChange"
        />
        <div v-if="selectedTimeframe === TimeframeOptions['CUSTOM']" class="date-range-container">
          <div class="date-input-container">
            <label for="start-date" class="date-label">{{ t('csvExport.startDate') }}</label>
            <input
              id="start-date"
              v-model="startDate"
              class="date-input"
              type="date"
              :max="endDate"
              :min="
                new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0]
              "
            />
          </div>
          <div class="date-input-container">
            <label for="end-date" class="date-label">{{ t('csvExport.endDate') }}</label>
            <input
              id="end-date"
              v-model="endDate"
              class="date-input"
              type="date"
              :max="new Date().toISOString().split('T')[0]"
              :min="startDate"
            />
          </div>
        </div>
        <h4 class="file-format-title">
          {{ t(`agency.costReport.fileFormat`) + ':' }}
        </h4>
        <div class="file-format-options">
          <HgRadioButton
            v-model="fileFormatModel"
            name="fileFormatRadioGroup"
            inputValue="xlsx"
            :label="t('agency.costReport.xlsxInfo')"
          />

          <HgRadioButton
            v-model="fileFormatModel"
            name="fileFormatRadioGroup"
            inputValue="csv"
            :label="t('agency.costReport.csvInfo')"
          />
        </div>
      </div>
      <div v-else class="spinner-container">
        <HgSpinner />
      </div>
    </template>
    <template #footer>
      <HgButton
        data-cy="modal-csv-export-close-btn"
        :disabled="isLoading"
        theme="secondary-bordered"
        @click="userClose()"
      >
        {{ t(`general.buttons.cancel`) }}
      </HgButton>
      <HgButton
        data-cy="modal-csv-export-action-btn"
        :disabled="isLoading || (selectedTimeframe === 'custom' && !validateInput)"
        @click="onActionButton"
      >
        {{ t('general.buttons.exportLeads') }}
      </HgButton>
    </template>
  </HgModal>
</template>

<script lang="ts" setup>
import { ContactRequestParams, DateRange } from '@/api/ubmContactRequestCsvExportService';
import { formatListingAddress } from '@/helpers/Address/Address';
import {
  TimeframeOptions,
  fetchAndDownloadLeadsExport,
  getDateRangeFromTimeframe,
} from '@/helpers/fetchAndDownloadLeadsExport';
import {
  EventAction,
  EventLabel,
  TimeFrameOptionsForTracking,
} from '@/services/tracking/GtmTrackingService';
import { trackClickedElement } from '@/services/tracking/Tracking';
import { ToastTheme } from '@/store/app/AppStore';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { FileFormat } from '@/types';
import { HgButton, HgModal, HgRadioButton, HgSpinner } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Modal, ModalIds } from '../ModalManager/types';
import TimeframeOptionsRadio from '../TimeframeOptionsRadio/TimeframeOptionsRadio.vue';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const fileFormatModel = ref<FileFormat>('xlsx');

const withPropertyReferenceId = computed(() => {
  const exportModal = store.getters.getModals.find(
    (modal: Modal) => modal.id === ModalIds.ExportCsvModal,
  );
  return exportModal?.options?.withPropertyReferenceId as boolean;
});

const emit = defineEmits(['update:modelValue', 'onClose']);

const store = useStore();
const agencyStore = useAgencyStore();
const { activeAgencyId } = storeToRefs(agencyStore);
const { t, locale } = useI18n();
const selectedTimeframe = ref<TimeframeOptions>(TimeframeOptions['7D']);
const isLoading = ref<boolean>(false);
const validateInput = ref<boolean>(true);
const timeFrame = ref<DateRange>({
  startDate: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
} as DateRange);

const startDate = computed({
  get() {
    return timeFrame.value.startDate;
  },
  set(value: string) {
    timeFrame.value.startDate = value;
    validateInput.value = areDatesValid();
  },
});

const endDate = computed({
  get() {
    return timeFrame.value.endDate;
  },
  set(value: string) {
    timeFrame.value.endDate = value;
    validateInput.value = areDatesValid();
  },
});

const isDateValid = (date: string) => {
  return !isNaN(Date.parse(date));
};

const areDatesValid = () => {
  return isDateValid(timeFrame.value.startDate!) && isDateValid(timeFrame.value.endDate!);
};

const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
    }
  },
);

const resetData = () => {
  isLoading.value = false;
  selectedTimeframe.value = TimeframeOptions['7D'];
  timeFrame.value = {
    startDate: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  } as DateRange;
  startDate.value = timeFrame.value.startDate!;
  endDate.value = timeFrame.value.endDate!;
};

const userClose = () => {
  trackClickedElement({
    action: EventAction.CLOSE_EXPORT_CSV_MODAL,
    label: 'undefined',
  });

  onClose();
};

const onClose = () => {
  if (isLoading.value) return;

  emit('onClose');
};

const dispatchSuccessToast = () => {
  store!.dispatch('showToast', {
    title: t('csvExport.csvExportSuccess'),
    message: t('csvExport.downloadStartsSoon'),
    theme: ToastTheme.Success,
  });
};

const dispatchFailureToast = () => {
  store!.dispatch('showToast', {
    title: t('csvExport.csvExportFailed'),
    message: t('csvExport.unexpectedErrorPlsTryAgain'),
    theme: ToastTheme.Danger,
  });
};

const trackCsvDownload = (success: boolean, startTime: Date): void => {
  trackClickedElement({
    action: success ? EventAction.EXPORT_CSV_SUCCESS : EventAction.EXPORT_CSV_FAIL,
    label: EventLabel.EXPORT_CSV,
    extraInformation: Math.round((Date.now() - startTime.getTime()) / 1000).toString(),
  });
};

const onActionButton = async () => {
  isLoading.value = true;
  const startRequestTime = new Date();
  let dateRange;

  try {
    dateRange =
      selectedTimeframe.value === TimeframeOptions['CUSTOM']
        ? timeFrame.value
        : getDateRangeFromTimeframe(selectedTimeframe.value);

    const propertyReferenceId = store.getters.getInsertionObjectReference;

    const apiParams = {
      listerId: activeAgencyId.value,
      fileFormat: fileFormatModel.value,
      lang: locale.value,
      ...dateRange,
    } as ContactRequestParams;

    const address = formatListingAddress(store.getters.getInsertion?.listing);
    if (address) {
      apiParams.address = address;
    }
    if (withPropertyReferenceId.value) {
      apiParams.propertyReferenceId = propertyReferenceId;
    }

    trackClickedElement({
      action: EventAction.EXPORT_CSV,
      label: 'undefined',
      extraInformation: TimeFrameOptionsForTracking[selectedTimeframe.value],
    });

    await fetchAndDownloadLeadsExport(t, apiParams);

    trackCsvDownload(true, startRequestTime);

    dispatchSuccessToast();
  } catch (e) {
    trackCsvDownload(false, startRequestTime);
    dispatchFailureToast();
  }
  isLoading.value = false;
  onClose();
};

const handleTimeframeChange = (value: TimeframeOptions) => {
  selectedTimeframe.value = value;
};
</script>

<style scoped>
.time-frame-title {
  width: 484px;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: bold;
}

.spinner-container {
  padding-right: 50%;
  padding-bottom: 10%;
  padding-left: 50%;
}

.file-format-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}

.date-range-container {
  display: flex;
  justify-content: space-between;
}

.date-input-container {
  display: flex;
  flex-direction: column;
  width: 226px;
  padding: 1rem 0 0 0;
  margin-right: 1rem;
}

.date-input {
  padding: 0.5rem;
  margin-right: 0.5rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.date-label {
  padding: 0 0 0.5rem 0;
}
</style>
